import { Link } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { motion } from 'framer-motion';

const PageHeader = ({ title, subtitle, breadcrumbs = [], backgroundImage, keywordsTier }) => {
  return (
    <section 
      className="relative py-24 md:py-32 overflow-hidden"
      style={{
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      data-testid="page-header"
    >
      {/* Dark Premium Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
      
      {/* Image Overlay if background image exists */}
      {backgroundImage && (
        <div className="absolute inset-0 bg-slate-950/70" />
      )}
      
      {/* Pattern Overlay */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Gradient Accents */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#C5A028]/5 to-transparent" />
      <div className="absolute bottom-0 left-0 w-1/2 h-32 bg-gradient-to-t from-[#C5A028]/5 to-transparent" />

      <div className="container max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Breadcrumbs */}
        {breadcrumbs.length > 0 && (
          <motion.nav 
            className="flex items-center gap-2 text-sm text-slate-400 mb-6"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link to="/" className="hover:text-[#C5A028] transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" />
              Home
            </Link>
            {breadcrumbs.map((crumb, index) => (
              <span key={index} className="flex items-center gap-2">
                <ChevronRight className="w-4 h-4 text-slate-400" />
                {crumb.path ? (
                  <Link to={crumb.path} className="hover:text-[#C5A028] transition-colors">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-white">{crumb.label}</span>
                )}
              </span>
            ))}
          </motion.nav>
        )}

        {/* Title */}
        <motion.h1 
          className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-syne mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <span className="block">{title}</span>
          {/* Optional pipe-separated keyword tier — kept inside the same H1 so
              search engines read it as a single heading, but visually
              de-emphasised so the primary brand line stays dominant. */}
          {keywordsTier && Array.isArray(keywordsTier) && keywordsTier.length > 0 && (
            <span
              className="block text-base md:text-lg lg:text-xl font-medium font-manrope text-slate-300 mt-3 tracking-normal"
              data-testid="page-header-keywords-tier"
            >
              {keywordsTier.map((kw, idx) => (
                <span key={kw}>
                  <span className={idx === 0 ? 'text-[#C5A028]' : 'text-white'}>{kw}</span>
                  {idx < keywordsTier.length - 1 && (
                    <span className="mx-2 text-slate-500" aria-hidden="true">|</span>
                  )}
                </span>
              ))}
            </span>
          )}
        </motion.h1>

        {/* Subtitle */}
        {subtitle && (
          <motion.p 
            className="text-lg md:text-xl text-slate-400 max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {subtitle}
          </motion.p>
        )}

        {/* Gold accent line */}
        <motion.div 
          className="mt-8 h-1 w-24 bg-gradient-to-r from-[#C5A028] to-[#EAB308] rounded-full"
          initial={{ scaleX: 0, originX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        />
      </div>

      {/* Bottom fade to dark */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-slate-950 to-transparent" />
    </section>
  );
};

export default PageHeader;
